import React, {useCallback, useEffect, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Lightbox, {ImagesListType} from 'react-spring-lightbox';
import LightboxHeader from '../LightboxHeader';
import {Thumb} from '../embla/EmblaCarouselThumb';
import Image from 'next/image';
import Router from 'next/router';

export function EmblaGallery({
                               pictures,
                               slides,
                               height = 'h-32rem',
                               width = 'w-auto',
                               showThumbnails = true,
                               priority = true,
                               lazy = false,
                               productUrl,
                               routeToProductOnClick = false,
                             }) {

  const [galleryIndex, setGalleryIndex] = useState(0);

  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const [touchPosition, setTouchPosition] = useState(null);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);

  const [mainViewportRef, embla] = useEmblaCarousel({skipSnaps: false});
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    selectedClass: '',
    dragFree: true,
  });

  const onThumbClick = useCallback(
      (index) => {
        if (!embla || !emblaThumbs) return;
        if (emblaThumbs.clickAllowed()) embla.scrollTo(index);

        setGalleryIndex(index);
      },
      [embla, emblaThumbs],
  );

  const onSelect = useCallback(() => {
    if (!embla || !emblaThumbs) return;
    setGalleryIndex(embla.selectedScrollSnap());
    emblaThumbs.scrollTo(embla.selectedScrollSnap());
  }, [embla, emblaThumbs, setGalleryIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on('select', onSelect);
  }, [embla, onSelect]);

  const images = pictures.map(picture => {

    const {url, alternativeText, formats} = picture;
    const mediumUrl = !!formats.medium ?
        formats.medium.url :
        url;

    return {
      src: url,
      mediumUrl,
      alt: alternativeText,
      loading: 'lazy',
      ...picture,
    };

  });

  const showDirections = images.length > 1;

  if (!Array.isArray(pictures) || pictures.length <= 0) return null;

  const onClickNext = () => {

    const newIndex = galleryIndex === pictures.length - 1 ?
        0 :
        galleryIndex + 1;

    setGalleryIndex(newIndex);

    onThumbClick(newIndex);

  };

  const onClickPrev = () => {

    const newIndex = galleryIndex === 0 ?
        pictures.length - 1 :
        galleryIndex - 1;

    setGalleryIndex(newIndex);
    onThumbClick(newIndex);

  };

  const onClose = (e) => {
  };

  const handleOnClickImage = async (e) => {
    e.preventDefault();
    if (routeToProductOnClick && !!productUrl) {
      await Router.push(productUrl);
    } else {
      setLightboxIsOpen(true);
    }
  };

  const multiImagesExists = images.length > 1;

  return (
      <>
        <Lightbox
            isOpen={lightboxIsOpen}
            onPrev={onClickPrev}
            onNext={onClickNext}
            images={images}
            currentIndex={galleryIndex}
            renderHeader={() => <LightboxHeader
                setLightboxIsOpen={setLightboxIsOpen}/>}
            renderPrevButton={() => (showDirections &&
                <CustomPrevArrowButton onClickPrev={onClickPrev}
                                       className={'ml-4 ml-10 shadow-2xl'}/>)}
            renderNextButton={() => (showDirections &&
                <CustomNextArrowButton onClickNext={onClickNext}
                                       className={'mr-4 md:mr-10 shadow-2xl'}/>)}
            style={{background: '#000000'}} onClose={onClose}/>

        <div className={`relative w-full mx-auto mb-5 ${height}`}>
          <div className="w-full h-full overflow-x-hidden"
               ref={mainViewportRef}>
            <div className="embla__container h-full">
              {images.map((image, index) => {

                const {alt, formats, src} = image;
                const _src = !!formats.medium ? formats.medium.url : src;

                return <div className="min-w-full h-full relative" key={index}>
                  <div className="embla__slide__inner h-full">
                    <div
                        className="max-w-md lg:max-w-2xl cursor-pointer"
                        onClick={handleOnClickImage}
                    >
                      <Image
                          src={_src}
                          alt={alt}
                          layout={'fill'}
                          objectFit="cover"
                          objectPosition={'center'}
                          priority={priority}
                          loading={lazy ? 'lazy' : 'eager'}
                          quality={100}/>
                    </div>
                  </div>
                </div>;
              })}
            </div>
            {!!multiImagesExists &&
            <CustomPrevArrowButton onClickPrev={onClickPrev}
                                   className={'bg-opacity-80 backdrop-filter backdrop-blur-md'}/>}
            {!!multiImagesExists &&
            <CustomNextArrowButton onClickNext={onClickNext}
                                   className={'bg-opacity-80 backdrop-filter backdrop-blur-md'}/>}
          </div>
        </div>

        {!!images &&
        <div className={`embla bg-gray-50 ${!showThumbnails && 'hidden'}`}>
          <div className="embla__viewport" ref={thumbViewportRef}>
            <div className="embla__container embla__container--thumb">
              {images.map((image, index) => (
                  <Thumb
                      onClick={() => onThumbClick(index)}
                      selected={index === galleryIndex}
                      img={image}
                      imgSrc={image.src}
                      key={index}
                  />
              ))}
            </div>
          </div>
        </div>}
      </>
  );

}

const CustomPrevArrowButton = ({onClickPrev, className = ''}) => {

  return <div onClick={onClickPrev}
              className={'bg-gray-200 hover:bg-gray-700 p-2 rounded-full h-8 w-8 hover:text-gray-200 text-gray-800  z-10 transition absolute left-3 top-1/2 cursor-pointer ' +
              className}>
    <svg
        className={'w-auto h-auto'}
        fill="none" stroke="currentColor"
        viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 19l-7-7 7-7"/>
    </svg>
  </div>;

};

const CustomNextArrowButton = ({onClickNext, className = ''}) => {

  return <div onClick={onClickNext}
              className={'bg-gray-200 hover:bg-gray-700 p-2 rounded-full h-8 w-8 hover:text-gray-200 text-gray-800  z-10 transition absolute right-3 top-1/2 cursor-pointer ' +
              className}>
    <svg
        className={'w-auto h-auto '}
        fill="none" stroke="currentColor"
        viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5l7 7-7 7"/>
    </svg>
  </div>;

};
